module Client.App

open Shared
open Illy.SafeStack.Client
open Elmish
open Elmish.React
open Elmish.Navigation
open Fable.Core.JsInterop
open Fable.React.Props
open Elmish.HMR
open SafeStack.Client
open Routing
open Fable.React

type PageModel =
    | NoPage
    | Login of Login.Model
    | ResetPassword of ResetPassword.Model
    | Home of Home.Index.Model
    | MFA of MFA.Model

module PageModel =
    let canReauthenticateOnPage =
        function
        | Login _
        | MFA _
        | ResetPassword _ -> false
        | _ -> true

type Model =
    {
        IsSSO: bool
        CurrentPage: PageModel
        Reauthenticate: Reauthenticate.Model option
        ApiError: AppError option
    }

type Msg =
    | LoginMsg of Login.Msg
    | ResetPasswordMsg of ResetPassword.Msg
    | HomeMsg of Home.Index.Msg
    | ReauthenticateMsg of Reauthenticate.Msg
    | MFAMsg of MFA.Msg
    | UserStored
    | UserUpdatedAfterReauthentication
    | CloseErrorOverlay
    | NotificationWasHidden
    | ReloadPage
    | LoadedContext of LoginContext

module Calls =
    let getContext() =
        Api.unsecureCallSuccess<ILoginApi,_,_,_>
            (fun api -> api.loadContext)
            ()
            LoadedContext

let urlUpdate pageRoute model =
    match pageRoute with
    | None ->
        printfn "No route found for the url"
        let loginModel, loginCmd = 
            Login.init()
    
        { model with
            CurrentPage = Login loginModel
        }, Cmd.map LoginMsg loginCmd
    | Some PageRoute.Login ->
        let loginModel, loginCmd = 
            Login.init()
    
        { model with
            CurrentPage = Login loginModel
        }, Cmd.map LoginMsg loginCmd
    | Some (PageRoute.ResetPassword token)->
        let m, c = ResetPassword.init token
        {model with CurrentPage = ResetPassword m},
        Cmd.batch [
            Cmd.map ResetPasswordMsg c
            Calls.getContext()
        ]
    | Some PageRoute.Home ->
        let homeModel, homeCmd = Home.Index.init()
    
        { model with
            CurrentPage = Home homeModel
        },
        Cmd.batch [
            Cmd.map HomeMsg homeCmd
            Calls.getContext()
        ]
    | Some PageRoute.EnterMFA ->
        let mModel, mCmd = MFA.init()
        {
            model with
                CurrentPage = MFA mModel
        },
        Cmd.batch [
            Cmd.map MFAMsg mCmd
            Calls.getContext()
        ]

let init (page: PageRoute option) = urlUpdate page { CurrentPage = NoPage; ApiError = None; Reauthenticate = None; IsSSO = false }

let dispatchAppError model error =
    match error with
    | AppError.ApiError (ApiError.ProxyError err) ->
        if err.StatusCode = 401 && (PageModel.canReauthenticateOnPage model.CurrentPage) then
            let u = Storage.get<ClientSideUser> Storage.USER_STORAGE_KEY
            let m, c = Reauthenticate.init u.IsIlly model.IsSSO u.Username
            {model with Reauthenticate = Some m}, Cmd.map ReauthenticateMsg c
        else
            {model with ApiError = Some error}, Cmd.none
    | AppError.ApiError (ApiError.AsyncError _)
    | AppError.ItemWasDeleted _ ->
        {model with ApiError = Some error}, Cmd.none
    | AppError.ApiError (ApiError.NoUser) ->
        model, Routing.PageRoute.Login |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl

let dispatchReauthenticateMsg model msg =
    match model.Reauthenticate with
    | None -> model, Cmd.none
    | Some rm ->
        let m, eff = Reauthenticate.update msg rm
        match eff with
        | SideEffect.Cmd c -> {model with Reauthenticate = Some m}, Cmd.map ReauthenticateMsg c
        | SideEffect.Event ev ->
            match ev with
            | Reauthenticate.Event.CloseOverlay ->
                {model with Reauthenticate = None}, Cmd.none
            | Reauthenticate.Event.SaveSuccessful (u, notification) ->
                {model with Reauthenticate = None},
                Cmd.batch [
                    Cmd.OfAsync.perform (Storage.store Storage.USER_STORAGE_KEY) u (fun () -> UserUpdatedAfterReauthentication)
                    Notification.success notification NotificationWasHidden
                ]
            | Reauthenticate.Event.ApiError err ->
                { model with ApiError = Some (AppError.ApiError err) }, Cmd.none

let update msg model =
    match msg with
    | LoginMsg lmsg ->
        match model.CurrentPage with
        | PageModel.Login login ->
            let lmod, lcmd = Login.update lmsg login
            {model with CurrentPage = Login lmod}, Cmd.map LoginMsg lcmd
        | _ -> model, Cmd.none
    | MFAMsg mmsg ->
        match model.CurrentPage with
        | PageModel.MFA mfa ->
            let mmod, mcmd = MFA.update mmsg mfa
            { model with CurrentPage = MFA mmod }, Cmd.map MFAMsg mcmd
        | _ -> model, Cmd.none
    | ResetPasswordMsg lmsg ->
        match model.CurrentPage with
        | PageModel.ResetPassword rmod ->
            let lmod, eff = ResetPassword.update lmsg rmod
            match eff with
            | SideEffect.Cmd c ->
                {model with CurrentPage = ResetPassword lmod}, Cmd.map ResetPasswordMsg c
            | SideEffect.Event apiErr ->
                dispatchAppError {model with CurrentPage = ResetPassword lmod} (AppError.ApiError apiErr)
        | _ -> model, Cmd.none
    | HomeMsg hmsg ->
        match model.CurrentPage with
        | PageModel.Home home ->
            let hmod, eff = Home.Index.update hmsg home
            match eff with
            | SideEffect.Cmd c ->
                {model with CurrentPage = Home hmod}, Cmd.map HomeMsg c
            | SideEffect.Event appError ->
                dispatchAppError {model with CurrentPage = Home hmod} appError
        | _ -> model, Cmd.none
    | ReauthenticateMsg rMsg -> dispatchReauthenticateMsg model rMsg
    | NotificationWasHidden -> model, Cmd.none
    | UserStored -> model, Cmd.none
    | CloseErrorOverlay -> {model with ApiError = None}, Cmd.none
    | UserUpdatedAfterReauthentication -> model, Cmd.ofMsg ReloadPage
    | ReloadPage ->
        Browser.Dom.window.location?reload()    
        model, Cmd.none
    | LoadedContext ctx ->
        {
            model with
                IsSSO = ctx.SupportsOIDC
        }, Cmd.none    

let renderProxyErrorOverlay (err: ProxyError) dispatch =
    jQueryUI.modal "Something went wrong..." 8000 CloseErrorOverlay dispatch (Some 800) [
        match err.ErrorMessage with
        | None -> 
            yield str "Something went wrong behind the scenes, but it was not possible to log what happened."
            yield br []
            yield str (sprintf "The server returned status code %d" err.StatusCode)

        | Some errMsg ->
            yield str errMsg
        yield! UI.saveControls (Some "Reload Page") false false ReloadPage CloseErrorOverlay dispatch
    ]

let renderAsyncErrorOverlay dispatch =
    jQueryUI.modal "Something went wrong..." 8000 CloseErrorOverlay dispatch (Some 800)
        [
            str "Something went wrong behind the scenes when performing a request to the server. "
            br []
            str "It was not possible to log what happened."
            yield! UI.saveControls (Some "Reload Page") false false ReloadPage CloseErrorOverlay dispatch
        ]


let renderItemWasDeletedOverlay entityName dispatch =
    jQueryUI.modal "Another user has deleted this item" 8000 CloseErrorOverlay dispatch (Some 800)
        [
            jQueryUI.illy_info  
                [ 
                    str (sprintf "Another user has deleted this %s while you were updating it. Your changes have not been saved." entityName) 
                ]

            div [ ClassName "saveControls"] [
                    a [ OnClick (fun _ -> dispatch CloseErrorOverlay)] [ str "Close" ] 
                ]
        ]

let renderApiError apiErr dispatch =
    match apiErr with
    | ApiError.ProxyError err -> renderProxyErrorOverlay err dispatch
    | ApiError.AsyncError _ -> renderAsyncErrorOverlay dispatch
    | ApiError.NoUser -> [ str "No user found. Logging out..." ]

let renderAppError appError dispatch =
    match appError with
    | AppError.ApiError err -> renderApiError err dispatch
    | AppError.ItemWasDeleted item -> renderItemWasDeletedOverlay item dispatch

let view model dispatch =
    div [] [
        match model.CurrentPage with
        | NoPage -> div [] []
        | Login login -> Login.view login (LoginMsg >> dispatch)
        | ResetPassword m -> ResetPassword.view m (ResetPasswordMsg >> dispatch)
        | Home home -> Home.Index.view home (HomeMsg >> dispatch)
        | MFA mfa -> MFA.view mfa (MFAMsg >> dispatch)
        yield! jQueryUI.conditionallyRenderView model.ApiError renderAppError id dispatch
        yield! jQueryUI.conditionallyRenderView model.Reauthenticate Reauthenticate.view ReauthenticateMsg dispatch
    ]

let subscribe model =
    Sub.batch [
        Sub.map "tickTimeSince" HomeMsg (Home.Index.subscribe model)
    ] 

Program.mkProgram init update view
|> Program.withSubscription subscribe
|> Program.toNavigable Routing.urlParser urlUpdate
#if DEBUG
|> Program.withConsoleTrace
#endif
|> Program.withReactSynchronous "elmish-app"
|> Program.run
